import { openToastr } from '@Common/handlers/services';
import { RootState } from '@Common/models/redux/store';
import { Button, CircularProgress, Theme, useMediaQuery } from '@quillbot/design-system';
import { generateRandomId } from 'core/utils';
import React, { RefObject, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCitationGeneratorDispatch,
  useCitationGeneratorState,
} from 'src/citation-generator/context';
import {
  stopSearchMode,
  toggleCitationGeneratorEditModal,
  toggleInplaceCreateForm,
} from 'src/citation-generator/context/actionCreators';
import citationGeneratorTrack from 'src/citation-generator/handlers/track';
import {
  addCitationToList,
  rebuildCitationNumbers,
  updateCitedData,
} from 'src/citation-generator/reducers/actionCreators';
import { CitationData } from 'src/citation-generator/types';
import { TriggeringActions } from 'src/citation-generator/types/names';
import {
  convertCitedDataResponse,
  getCgBreakpointDown,
  scrollIntoViewCitationCard,
} from 'src/citation-generator/utils';
import { updateCitationMarkerForThisCitation } from 'src/researcher/component/citationTab/utils';
import { generateCitations } from 'src/researcher/handlers/api';
import { isString, replaceSpaces } from 'src/researcher/utils';
import { getApplicableFields } from '../config';
import { useEditFormDispatch, useEditFormState } from '../context';
import { resetState, setErrorAndWarning, setTextValueOf } from '../actionCreators';
import { validators } from '../utils';

export function SaveButton({ topElRef }: { topElRef: RefObject<HTMLDivElement> }) {
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));
  const [isCitedDataLoading, setIsCitedDataLoading] = useState(false);
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);
  const dispatch = useDispatch();
  const { errors, ...formState } = useEditFormState((state) => state);
  const editFormDispatch = useEditFormDispatch();
  const isCited = useCitationGeneratorState((state) => state.editFormDefaultValuse?.isCited);
  const indexOfCitaionToBeEdit = useCitationGeneratorState((state) => state.indexOfCitaionToBeEdit);
  const citationGeneratorDispatch = useCitationGeneratorDispatch();

  const onSave = () => {
    if (isCitedDataLoading) {
      return;
    }

    {
      const isEmpty = validators.error.empty(formState.title);

      if (isEmpty) {
        editFormDispatch(
          setErrorAndWarning({
            key: 'title',
            error: Array.isArray(isEmpty) ? isEmpty[0]?.error : isEmpty.error,
          })
        );
        editFormDispatch(setTextValueOf('title', ''));
        topElRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
        if (isSmallScreen) {
          window.scrollTo(0, 0);
        }
        return;
      }
    }

    if (Object.keys(errors).length) {
      return;
    }

    const fields = getApplicableFields(formState.sourceType, citationStyle);
    const dispatchPayload: CitationData = {
      id: formState.id,
      refId: formState.refId || generateRandomId(),
      citationSource: formState.sourceType,
      publicationInfo: {},
      contributors: [],
      isCited: isCited || false,
    };
    fields.forEach((field) => {
      switch (field) {
        case 'contributors':
          dispatchPayload.contributors =
            formState.contributors?.filter((c) => c.firstName || c.lastName) || [];
          break;
        case 'sourceType':
          break;
        default: {
          let value = formState[field];

          if (isString(value)) {
            value = replaceSpaces(formState[field] as string).trim();
          }

          dispatchPayload.publicationInfo[field] = value as any;
          break;
        }
      }
    });

    setIsCitedDataLoading(true);
    generateCitations({ style: citationStyle, citations: [dispatchPayload] })
      .then((response) => {
        if (response.style !== citationStyle) {
          // TODO: show/throw error
        }
        const citedData = convertCitedDataResponse(response);
        dispatch(updateCitedData(citedData));
        dispatch(addCitationToList(dispatchPayload, indexOfCitaionToBeEdit));
        dispatch(rebuildCitationNumbers());
        citationGeneratorDispatch(stopSearchMode());
        scrollIntoViewCitationCard(dispatchPayload.refId);
        if (isSmallScreen) {
          window?.scrollTo(0, 0);
        }
        citationGeneratorDispatch(toggleCitationGeneratorEditModal(false));
        citationGeneratorDispatch(toggleInplaceCreateForm(false));
        updateCitationMarkerForThisCitation(dispatchPayload, citedData[0]);
        editFormDispatch(resetState());
      })
      .catch((e: any) => {
        openToastr('Unable to use selected citation style!');
      })
      .finally(() => {
        setIsCitedDataLoading(false);
      });
    citationGeneratorTrack.userClickedAddCitation(TriggeringActions.SAVE_BUTTON);
  };

  return (
    <Button
      color="primary"
      variant="contained"
      onClick={onSave}
      style={{
        fontWeight: 600,
        height: '30px',
        borderRadius: '50px',
      }}
    >
      <span
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isCitedDataLoading ? <CircularProgress size="18px" color="inherit" /> : 'Save Citation'}
      </span>
    </Button>
  );
}
